import {
  AlertCircle,
  Bookmark,
  BookmarkCheck,
  CalendarDays,
} from "lucide-react";
import { useMemo, useState } from "react";

import { EmptyState, LoadingCards } from "components/states";
import { ToggleChip } from "components/ToggleChip";
import { Button } from "components/ui/button";

import {
  dayKeyOf,
  formatDayChip,
  toDate,
  usableTimeZone,
} from "./agenda-schedule";
import { bookmarkTitle } from "./bookmark-title";
import { AgendaSchedule } from "./AgendaSchedule";
import {
  dayFilterOptions,
  hasProgramme,
  type DayFilterOption,
} from "./programme";
import { ProgrammeSchedule } from "./ProgrammeSchedule";
import { isForAttendee } from "./now-next";
import type { AgendaItem } from "./schemas";
import { useBookmarks } from "./use-bookmarks";
import { useLiveAgenda, useTracks } from "./use-event-content";
import { YourTrackBanner } from "./YourTrackBanner";
import { TrackSwitchControl } from "../track-switch/TrackSwitchControl";
import type { AttendeeTrackSwitch } from "../track-switch/use-attendee-track-switch";

/**
 * Which sessions the agenda lists. "mine" is the attendee's own track plus
 * every session with no track, the plenaries. When no session is in the
 * attendee's track it lists nothing and says so: a list of plenaries under a
 * chip called "My track" would read as the track's programme. (The
 * now-and-next card filters nothing; it only ranks the attendee's first.)
 * "day" lists one day of the event, numbered as the day headers are.
 */
type AgendaFilter =
  | { kind: "all" }
  | { kind: "schedule" }
  | { kind: "mine" }
  | { kind: "day"; dayKey: string };

const ALL: AgendaFilter = { kind: "all" };

export function AgendaSection({
  eventId,
  uid,
  timeZone,
  myTrackId,
  trackSwitch,
  dayTitles,
  eventDates,
  startOnMySchedule = false,
}: {
  eventId: string;
  uid: string;
  /** The event's IANA zone, from the public projection, always valid. */
  timeZone: string;
  /** The attendee's own track from their member document, if assigned. */
  myTrackId?: string;
  /** TNG-92: table requests, offered from the banner while they are on. */
  trackSwitch?: AttendeeTrackSwitch;
  /**
   * The organiser's title per programme day, keyed by the day key. With these
   * or a highlighted session the tab uses the programme layout.
   */
  dayTitles?: Record<string, string> | null;
  /** The event's first and last date, which the programme's "Day N" counts from. */
  eventDates: { startDate: string; endDate: string };
  /**
   * Open with "My schedule" selected, as the Me tab's button asks. Read once,
   * when the tab mounts: the filter is the section's own state from then on.
   */
  startOnMySchedule?: boolean;
}) {
  const agenda = useLiveAgenda(eventId);
  const tracks = useTracks(eventId);
  const bookmarks = useBookmarks(uid, eventId);
  const [filter, setFilter] = useState<AgendaFilter>(
    startOnMySchedule ? { kind: "schedule" } : ALL,
  );

  // A session with no start time is a placeholder the organiser has not
  // finished, so attendees do not see it. The admin tab lists it under
  // "No time set", where the organiser can give it a time.
  const scheduled = useMemo(
    () =>
      agenda.status === "success"
        ? agenda.data.filter((item) => toDate(item.startsAt) !== null)
        : [],
    [agenda],
  );
  const bookmarkIds = useMemo(
    () =>
      new Set(
        bookmarks.state.status === "success"
          ? bookmarks.state.data.map((bookmark) => bookmark.agendaItemId)
          : [],
      ),
    [bookmarks.state],
  );
  const trackList = useMemo(
    () => (tracks.status === "success" ? tracks.data : []),
    [tracks],
  );
  // Only a track that exists: DELETE_TRACK clears the member's copy, but the
  // track list and the member document are read at different times.
  const myTrack = myTrackId
    ? trackList.find((track) => track.id === myTrackId)
    : undefined;
  const myTrackIds = useMemo(
    () => new Set(myTrack ? [myTrack.id] : []),
    [myTrack],
  );
  const zone = usableTimeZone(timeZone);
  // One chip per day with a session, none for a one-day event.
  const dayOptions = useMemo(
    () => dayFilterOptions(scheduled, zone, eventDates),
    [scheduled, zone, eventDates],
  );
  // Leave "My track" if the track goes away while it is selected, and a day
  // that no longer has a chip.
  const activeFilter = useMemo(() => {
    if (filter.kind === "mine" && !myTrack) return ALL;
    if (
      filter.kind === "day" &&
      !dayOptions.some((option) => option.key === filter.dayKey)
    )
      return ALL;
    return filter;
  }, [filter, myTrack, dayOptions]);
  const trackHasSessions = useMemo(
    () =>
      scheduled.some((item) =>
        (item.trackIds ?? []).some((id) => myTrackIds.has(id)),
      ),
    [scheduled, myTrackIds],
  );
  const filtered = useMemo(
    () =>
      scheduled.filter((item) => {
        switch (activeFilter.kind) {
          case "all":
            return true;
          case "schedule":
            return bookmarkIds.has(item.id);
          case "mine":
            return trackHasSessions && isForAttendee(item, myTrackIds);
          case "day": {
            const start = toDate(item.startsAt);
            return (
              start !== null && dayKeyOf(start, zone) === activeFilter.dayKey
            );
          }
        }
      }),
    [scheduled, activeFilter, bookmarkIds, myTrackIds, trackHasSessions, zone],
  );

  if (agenda.status === "loading") return <LoadingCards cards={3} />;
  if (agenda.status === "error")
    return (
      <EmptyState icon={AlertCircle} title="Could not load agenda">
        {agenda.error}
      </EmptyState>
    );
  if (scheduled.length === 0)
    return (
      <EmptyState icon={CalendarDays} title="No agenda items yet">
        The agenda will appear here once published.
      </EmptyState>
    );

  const showMySchedule = activeFilter.kind === "schedule";
  const dayFilter = activeFilter.kind === "day" ? activeFilter.dayKey : null;
  const programme = hasProgramme(scheduled, dayTitles);
  const renderBookmark = (item: AgendaItem, displayTitle?: string) => (
    <BookmarkControl
      item={item}
      title={displayTitle}
      bookmarked={bookmarkIds.has(item.id)}
      ready={bookmarks.state.status === "success"}
      pending={bookmarks.pendingIds.has(item.id)}
      onToggle={() => void bookmarks.toggle(item)}
    />
  );

  return (
    <div className="space-y-6" data-testid="agenda-content">
      {myTrack && (
        <YourTrackBanner eventId={eventId} track={myTrack}>
          {trackSwitch?.enabled && (
            <TrackSwitchControl
              eventId={eventId}
              tracks={trackList}
              currentTrackId={myTrack.id}
              request={trackSwitch.request}
              writes={trackSwitch.writes}
              accepting={trackSwitch.accepting}
            />
          )}
        </YourTrackBanner>
      )}

      <AgendaFilters
        days={dayOptions}
        filter={activeFilter}
        hasMyTrack={myTrack !== undefined}
        bookmarkCount={bookmarkIds.size}
        bookmarksReady={bookmarks.state.status === "success"}
        onAll={() => setFilter(ALL)}
        onMySchedule={() =>
          setFilter(showMySchedule ? ALL : { kind: "schedule" })
        }
        onMyTrack={() =>
          setFilter(activeFilter.kind === "mine" ? ALL : { kind: "mine" })
        }
        onDay={(dayKey) =>
          setFilter(dayKey === dayFilter ? ALL : { kind: "day", dayKey })
        }
      />

      {bookmarks.mutationError && (
        <p role="alert" className="text-sm text-destructive-text">
          {bookmarks.mutationError}
        </p>
      )}
      {bookmarks.state.status === "error" && (
        <p role="alert" className="text-sm text-destructive-text">
          My schedule could not be loaded: {bookmarks.state.error}
        </p>
      )}
      {tracks.status === "error" && (
        <p role="alert" className="text-sm text-destructive-text">
          Track details could not be loaded: {tracks.error}
        </p>
      )}

      {/* Not while the bookmarks load: a schedule asked for from the Me tab is
          selected before they arrive, and is not empty for it. */}
      {showMySchedule &&
        bookmarks.state.status === "success" &&
        filtered.length === 0 && (
          <EmptyState icon={Bookmark} title="Your schedule is empty">
            Bookmark agenda items to build your personal schedule.
          </EmptyState>
        )}
      {activeFilter.kind === "mine" && filtered.length === 0 && (
        <EmptyState icon={CalendarDays} title="No sessions for your track yet">
          Choose All to see the whole agenda.
        </EmptyState>
      )}

      {programme ? (
        <ProgrammeSchedule
          items={filtered}
          everything={scheduled}
          tracks={trackList}
          timeZone={timeZone}
          dayTitles={dayTitles}
          eventDates={eventDates}
          idPrefix="agenda"
          highlightTrackIds={myTrackIds}
          renderActions={renderBookmark}
        />
      ) : (
        <AgendaSchedule
          items={filtered}
          tracks={trackList}
          timeZone={timeZone}
          idPrefix="agenda"
          collapsibleDays={false}
          dayPicker={false}
          highlightTrackIds={myTrackIds}
          renderActions={renderBookmark}
        />
      )}
    </div>
  );
}

function AgendaFilters({
  days,
  filter,
  hasMyTrack,
  bookmarkCount,
  bookmarksReady,
  onAll,
  onMySchedule,
  onMyTrack,
  onDay,
}: {
  days: DayFilterOption[];
  filter: AgendaFilter;
  hasMyTrack: boolean;
  bookmarkCount: number;
  bookmarksReady: boolean;
  onAll: () => void;
  onMySchedule: () => void;
  onMyTrack: () => void;
  onDay: (dayKey: string) => void;
}) {
  const showMySchedule = filter.kind === "schedule";
  const dayFilter = filter.kind === "day" ? filter.dayKey : null;
  return (
    <div className="flex flex-wrap gap-2">
      <ToggleChip
        selected={filter.kind === "all"}
        onClick={onAll}
        className="rounded-md px-5 py-3 text-sm"
        data-testid="all-filter"
      >
        All
      </ToggleChip>
      {days.map((day) => (
        <ToggleChip
          key={day.key}
          selected={dayFilter === day.key}
          onClick={() => onDay(day.key)}
          className="min-h-11 rounded-md px-5 py-3 text-sm"
          // The visible words first, then the date, so the name still contains
          // the label (WCAG 2.5.3) and a reader can tell which day it is.
          aria-label={`${day.label}, ${formatDayChip(day.key)}`}
          data-testid={`day-filter-${day.key}`}
        >
          {day.label}
        </ToggleChip>
      ))}
      {hasMyTrack && (
        <ToggleChip
          selected={filter.kind === "mine"}
          onClick={onMyTrack}
          className="rounded-md px-5 py-3 text-sm"
          data-testid="my-track-filter"
        >
          My track
        </ToggleChip>
      )}
      <ToggleChip
        selected={showMySchedule}
        onClick={onMySchedule}
        disabled={!bookmarksReady}
        className="rounded-md px-5 py-3 text-sm"
        data-testid="my-schedule-filter"
      >
        My schedule ({bookmarkCount})
      </ToggleChip>
    </div>
  );
}

function BookmarkControl({
  item,
  title = item.title,
  bookmarked,
  ready,
  pending,
  onToggle,
}: {
  item: AgendaItem;
  /** The title the card shows, when it is not the session's own. */
  title?: string;
  bookmarked: boolean;
  ready: boolean;
  pending: boolean;
  onToggle: () => void;
}) {
  return (
    <span
      className="inline-flex shrink-0"
      title={bookmarkTitle({
        bookmarked,
        ready,
        pending,
      })}
      data-testid={`bookmark-hint-${item.id}`}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        // 44px square on a phone, the smallest target a thumb hits reliably.
        className="h-11 w-11 sm:h-9 sm:w-10"
        onClick={onToggle}
        disabled={!ready || pending}
        aria-label={
          bookmarked
            ? `Remove ${title} from my schedule`
            : `Add ${title} to my schedule`
        }
        aria-pressed={bookmarked}
        data-testid={`bookmark-${item.id}`}
      >
        {bookmarked ? (
          <BookmarkCheck className="h-4 w-4" />
        ) : (
          <Bookmark className="h-4 w-4" />
        )}
      </Button>
    </span>
  );
}
